import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Share2, ArrowRight, Calendar } from "lucide-react";
import Footer from "@/components/Footer";

export default function ConfirmationPage() {
  const [params] = useSearchParams();
  const tier = params.get("tier") || "early_bird";
  const isEarlyBird = tier === "early_bird";
  const price = "$29.99";

  const shareText = encodeURIComponent(
    "I just pre-ordered The Forty Year Bender by Chris Merlo. A raw, unfiltered story of addiction, rock bottom, and redemption. Launching June 4, 2026. Find your breakthrough."
  );

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="confirmation-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="font-brand text-2xl tracking-tighter text-white">FYB</Link>
        </div>
      </nav>

      <main className="pt-32 pb-24 px-6">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          {/* Success Icon */}
          <div className="flex justify-center animate-fade-in">
            <div className="w-20 h-20 bg-[#D4AF37]/10 border-2 border-[#D4AF37] rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
            </div>
          </div>

          {/* Confirmation Message */}
          <div className="space-y-4 opacity-0 animate-fade-in-up stagger-2">
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter uppercase text-white">
              You're In.
            </h1>
            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed">
              Your pre-order is confirmed at <span className="text-[#D4AF37] font-semibold">{price}</span>.
              {isEarlyBird && " You saved $10 by being early. Smart move."}
            </p>
          </div>

          <div className="gold-line" />

          {/* What Happens Next */}
          <div className="bg-[#121212] border border-white/10 p-8 sm:p-10 rounded-sm space-y-6 text-left opacity-0 animate-fade-in-up stagger-3">
            <h2 className="font-heading text-2xl tracking-tight uppercase text-white">What Happens Next</h2>
            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="font-heading text-sm text-[#D4AF37]">1</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Confirmation Email</p>
                  <p className="text-zinc-400 text-sm">You'll receive a confirmation email with your order details shortly.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="font-heading text-sm text-[#D4AF37]">2</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Launch Day &mdash; June 4, 2026</p>
                  <p className="text-zinc-400 text-sm">We'll redirect you to the retailer for final purchase when the book goes live.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="font-heading text-sm text-[#D4AF37]">3</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Get Your Bonuses</p>
                  <p className="text-zinc-400 text-sm">{isEarlyBird ? "Your free Clarity Mini-Guide and Q&A invite will arrive by email." : "Stay tuned for bonus content closer to launch."}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Share */}
          <div className="space-y-4 opacity-0 animate-fade-in-up stagger-4">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-bold">Spread The Word</p>
            <p className="text-zinc-400 text-sm">Know someone who needs this book? Share it.</p>
            <div className="flex justify-center gap-4">
              <a
                href={`https://twitter.com/intent/tweet?text=${shareText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/20 text-white px-6 py-3 text-xs tracking-[0.15em] uppercase rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all inline-flex items-center gap-2"
                data-testid="share-twitter"
              >
                <Share2 className="w-4 h-4" /> Share on X
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?quote=${shareText}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/20 text-white px-6 py-3 text-xs tracking-[0.15em] uppercase rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all inline-flex items-center gap-2"
                data-testid="share-facebook"
              >
                <Share2 className="w-4 h-4" /> Share on Facebook
              </a>
            </div>
          </div>

          {/* Back to home */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-zinc-500 text-sm hover:text-[#D4AF37] transition-colors"
            data-testid="confirmation-back-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Home
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
